// Publish / unpublish an archive article.
//
// `status` is the single membership visibility switch: flipping to
// 'excluded' pulls the article from the Briefing rails, search,
// related-article slots and its own member-facing page at once.
//
// Shared by the archive list and the article detail page so both invalidate
// the same query keys — a stale badge on the list after acting from the
// detail page (or vice versa) reads as the action having failed.

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'

export type ArticleStatus = 'active' | 'excluded' | 'stale'

export const STATUS_LABEL: Record<string, string> = {
  active: 'Published',
  excluded: 'Unpublished',
  stale: 'Gone from WP',
}

/** Border + text classes per state. Green reads as live, red as pulled,
 *  grey as "not ours to decide — WordPress lost the post". */
export const STATUS_CLASS: Record<string, string> = {
  active: 'border-radar-green/50 text-radar-green',
  excluded: 'border-radar-red/60 text-radar-red',
  stale: 'border-paper-trace text-paper-trace',
}

/** A retired row shares its status with others ('stale' with a WordPress
 *  deletion, 'excluded' with a hand unpublish); the reason tells them apart. */
const RETIRED_LABEL: Record<string, string> = {
  backcat_retired: 'Retired',
  out_of_date: 'Out of date',
}

export function statusLabel(status: string, reason?: string | null): string {
  const retired = reason ? RETIRED_LABEL[reason] : undefined
  if (retired && (status === 'stale' || status === 'excluded')) return retired
  return STATUS_LABEL[status] ?? status
}

export function usePublishArticle(articleId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (published: boolean) =>
      api.patch(`/api/lab/archive/article/${articleId}/status`, { published }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['archive-article', articleId] })
      qc.invalidateQueries({ queryKey: ['lab', 'archive'] })
    },
  })
}

/** The API's error detail, or a readable fallback. */
export function publishError(error: unknown): string | null {
  if (!error) return null
  const d = (error as { detail?: unknown }).detail
  return typeof d === 'string' ? d : 'Could not change the article’s status.'
}

// Scheduled publish. The editor picks a wall-clock time in US Eastern; the
// API converts it (so a laptop in another zone can't shift it) and the
// worker publishes within five minutes of it. `null` cancels.
export function useScheduleArticle(articleId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (publishAtLocal: string | null) =>
      publishAtLocal === null
        ? api.del(`/api/lab/archive/article/${articleId}/schedule`)
        : api.put(`/api/lab/archive/article/${articleId}/schedule`, {
            publish_at_local: publishAtLocal,
          }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['archive-article', articleId] })
      qc.invalidateQueries({ queryKey: ['lab', 'archive'] })
    },
  })
}

// Approval. An editor signs an unpublished article off; Publish and Schedule… are refused until
// then, and any content edit sends it back. Writers get 403.
export function useApproveArticle(articleId: number) {
  const qc = useQueryClient()
  const done = () => {
    qc.invalidateQueries({ queryKey: ['archive-article', articleId] })
    qc.invalidateQueries({ queryKey: ['lab', 'archive'] })
  }
  const approve = useMutation({
    mutationFn: () => api.put(`/api/lab/archive/article/${articleId}/approve`),
    onSuccess: done,
  })
  const unapprove = useMutation({
    mutationFn: () => api.del(`/api/lab/archive/article/${articleId}/approve`),
    onSuccess: done,
  })
  return { approve, unapprove }
}

export function approveError(error: unknown): string | null {
  if (!error) return null
  const d = (error as { detail?: unknown }).detail
  return typeof d === 'string' ? d : 'Could not change the approval.'
}

/** "Thu Oct 16, 6:00 AM ET" — always Eastern, whatever the browser's zone. */
export function fmtEastern(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    timeZone: 'America/New_York',
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  }) + ' ET'
}

/** A `datetime-local` value (YYYY-MM-DDTHH:MM) in Eastern: the saved time,
 *  else tomorrow at 6:00 AM. */
export function easternInputValue(iso: string | null | undefined): string {
  const parts = (d: Date) => Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/New_York', hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    }).formatToParts(d).map((p) => [p.type, p.value]),
  )
  if (iso) {
    const p = parts(new Date(iso))
    return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`
  }
  const p = parts(new Date(Date.now() + 24 * 3600 * 1000))
  return `${p.year}-${p.month}-${p.day}T06:00`
}

export function scheduleError(error: unknown): string | null {
  if (!error) return null
  const d = (error as { detail?: unknown }).detail
  return typeof d === 'string' ? d : 'Could not save the schedule.'
}
